import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const WhyWeHoldEventSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('ar');
  const [expandedSections, setExpandedSections] = useState([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const sectionContent = {
    ar: {
      title: "لماذا نحيي هذه الفعالية؟",
      sections: [
        {
          id: 'why-hold',
          title: 'لماذا نقيم هذه الفعالية؟',
          content: `يوم التحرير غيّر اتجاه البلد وفتح أمام السوريين شعورًا جديدًا بإمكانيّة التغيير.





الذكرى الأولى فرصة لتثبيت معنى ذلك اليوم في الوعي العام، لا أن نتركه يبهت مع الوقت.





استعادة الحدث تساعد على حفظ الحكاية كما عاشها الناس فعلًا، لا كما تُروى من بعيد.`
        },
        {
          id: 'meaning-to-syrians',
          title: 'ماذا يعني هذا اليوم للسوريين؟',
          content: `أيقظ التحرير شعور الانتماء بعد سنواتٍ من البعد والتشتّت.





رغم اختلاف التجارب، التقى السوريون على معنى واحد: الهوية ما زالت حيّة.





هذا اليوم أعاد النظر إلى سوريا بوصفها بيتًا يمكن العودة إليه وبناء مستقبله من جديد.`
        },
        {
          id: 'why-pivotal',
          title: 'لماذا كان يوم التحرير مفصليًا؟',
          content: `نقل وعي الناس من الغموض إلى فهمٍ أوضح للبلد ومساره.





مثّل نهاية زمن الانتظار الطويل وبداية فصل جديد في العلاقة مع الوطن.





أثبت أن التغيير ممكن، مهما طال الطريق وكبرت التحديات.`
        },
        {
          id: 'goal',
          title: 'ما الهدف من هذه الفعالية؟',
          content: `الحفاظ على الذاكرة ونقل القصة كما عاشها أصحابها.





جمع السوريين حول رؤية مشتركة للمستقبل الذي يريدونه لبلدهم.





التأكيد أن يوم التحرير ليس نهاية الحكاية، بل فصلٌ مفصليّ يحتاج أن نكمّله معًا بحضورٍ ووعيٍ من الجميع.`
        }
      ],
      closingStatement: "نلتقي لنحفظ الذاكرة، ونكمّل الحكاية مع الأجيال، ونمضي بخطوةٍ واضحة نحو المستقبل.",
      expandText: 'اقرأ المزيد',
      collapseText: 'اقرأ أقل'
    },
    en: {
      title: "Why are we holding this event?",
      sections: [
        {
          id: 'why-hold',
          title: 'Why are we holding this event?',
          content: `Liberation Day changed the country's direction and opened a new sense of possibility for change among Syrians.





The first anniversary is an opportunity to establish the meaning of that day in public consciousness, not to let it fade with time.





Reclaiming the event helps preserve the story as people actually lived it, not as it is told from afar.`
        },
        {
          id: 'meaning-to-syrians',
          title: 'What does this day mean to Syrians?',
          content: `Liberation awakened a sense of belonging after years of distance and dispersion.





Despite different experiences, Syrians converged on one meaning: identity is still alive.





This day restored the view of Syria as a home that can be returned to and whose future can be rebuilt.`
        },
        {
          id: 'why-pivotal',
          title: 'Why was Liberation Day pivotal?',
          content: `It shifted people's awareness from ambiguity to a clearer understanding of the country and its path.





It represented the end of a long period of waiting and the beginning of a new chapter in the relationship with the homeland.





It proved that change is possible, no matter how long the road or how great the challenges.`
        },
        {
          id: 'goal',
          title: 'What is the goal of this event?',
          content: `To preserve memory and transmit the story as those who lived it experienced it.





To gather Syrians around a shared vision for the future they want for their country.





To affirm that Liberation Day is not the end of the story, but a pivotal chapter that needs to be completed together with the presence and awareness of everyone.`
        }
      ],
      closingStatement: "We gather to preserve memory, complete the story with generations, and take a clear step toward the future.",
      expandText: 'Read More',
      collapseText: 'Read Less'
    },
    tr: {
      title: "Bu etkinliği neden düzenliyoruz?",
      sections: [
        {
          id: 'why-hold',
          title: 'Bu etkinliği neden düzenliyoruz?',
          content: `Kurtuluş Günü, ülkenin yönünü değiştirdi ve Suriyeliler için değişim olasılığına dair yeni bir duygu açtı.





İlk yıldönümü, o günün anlamını kamu bilincinde tespit etme fırsatıdır, zamanla solmasına izin vermemek için.





Olayı yeniden kazanmak, hikayeyi insanların gerçekten yaşadığı gibi korumaya yardımcı olur, uzaktan anlatıldığı gibi değil.`
        },
        {
          id: 'meaning-to-syrians',
          title: 'Bu gün Suriyeliler için ne anlama geliyor?',
          content: `Kurtuluş, uzaklık ve dağılma yıllarından sonra aidiyet duygusunu uyandırdı.





Farklı deneyimlere rağmen, Suriyeliler tek bir anlamda buluştu: kimlik hala canlı.





Bu gün, Suriye'ye geri dönülebilecek ve geleceğinin yeniden inşa edilebileceği bir ev olarak bakışı geri getirdi.`
        },
        {
          id: 'why-pivotal',
          title: 'Kurtuluş Günü neden dönüm noktasıydı?',
          content: `İnsanların bilincini belirsizlikten ülke ve yoluna dair daha net bir anlayışa taşıdı.





Uzun bir bekleme zamanının sonunu ve vatanla ilişkide yeni bir bölümün başlangıcını temsil etti.





Değişimin mümkün olduğunu kanıtladı, yol ne kadar uzun olursa olsun ve zorluklar ne kadar büyük olursa olsun.`
        },
        {
          id: 'goal',
          title: 'Bu etkinliğin amacı nedir?',
          content: `Belleği korumak ve hikayeyi yaşayanların deneyimlediği gibi aktarmak.





Suriyelileri ülkeleri için istedikleri gelecek hakkında ortak bir vizyon etrafında toplamak.





Kurtuluş Günü'nün hikayenin sonu değil, herkesin varlığı ve farkındalığıyla birlikte tamamlanması gereken önemli bir bölüm olduğunu onaylamak.`
        }
      ],
      closingStatement: "Belleği korumak, hikayeyi nesillerle tamamlamak ve geleceğe doğru net bir adım atmak için bir araya geliyoruz.",
      expandText: 'Devamını Oku',
      collapseText: 'Daha Az Oku'
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const content = sectionContent?.[currentLanguage];

  return (
    <section id="why-we-hold-event" className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            {content?.title}
          </h2>
        </motion.div>

        <div className="space-y-6">
          {content?.sections?.map((section, index) => (
            <motion.div
              key={section?.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-card border border-border rounded-lg overflow-hidden shadow-memorial"
            >
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full px-6 py-4 text-left memorial-transition hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-body text-lg md:text-xl font-semibold text-primary">
                    {section?.title}
                  </h3>
                  <Icon
                    name={expandedSections.includes(section?.id) ? 'ChevronUp' : 'ChevronDown'}
                    size={20}
                    className="text-text-secondary memorial-transition"
                  />
                </div>
              </button>

              {expandedSections.includes(section?.id) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 pb-6"
                >
                  <div className="border-t border-border pt-4">
                    <p className="font-body text-base text-text-primary leading-relaxed whitespace-pre-line">
                      {section?.content}
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Closing Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 border border-primary/20 rounded-lg p-6 md:p-8">
            <p className="font-body text-lg md:text-xl text-text-primary leading-relaxed font-medium">
              {content?.closingStatement}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyWeHoldEventSection;

